import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { getMergedNonMerchandisableArea } from "@CommonUtils/pog/shelfMerchandising";
import { Color, Flex } from "src/elements";
import styled from "styled-components";

const hatch = `
  background-color: ${Color.alertMediumBg};
  background-image: repeating-linear-gradient(
    135deg,
    transparent,
    transparent 5px,
    ${Color.alertMediumBorder} 5px,
    ${Color.alertMediumBorder} 8px
  );
`;

// Covers just the shelf's own physical thickness, right at the shelf's anchor line — the same
// domain span for every shelf type. Kept opaque since it sits directly over the shelf's solid
// visual, matching how the disabled shelf area originally looked.
const ShelfPart = styled(Flex)<{
  $scaleX: number;
  $scaleY: number;
  $width: number;
  shelf: PlanogramShelf;
}>`
  position: absolute;
  box-sizing: border-box;
  right: 0;
  bottom: 0;
  width: ${({ $scaleX, $width }) => `calc(${$width}px * ${$scaleX})`};
  height: ${({ shelf, $scaleY }) => `calc(${shelf.thickness}px * ${$scaleY})`};
  border-left: 1px solid ${Color.alertMediumIcon};
  ${hatch}
  opacity: 0.9;
  pointer-events: none;
  z-index: 3;
`;

// Covers the extended item-placement zone above the shelf's own thickness (or, for HANGCELL,
// below the shelf's anchor line, down to the shelf below/bay floor). Kept more transparent than
// the shelf part so overflowing products remain easy to see through it.
const ExtensionPart = styled(Flex)<{
  $scaleX: number;
  $scaleY: number;
  $width: number;
  shelf: PlanogramShelf;
}>`
  position: absolute;
  box-sizing: border-box;
  right: 0;
  bottom: ${({ shelf, $scaleY }) =>
    `calc(${shelf.type === "HANGCELL" ? -shelf.getHeight() : shelf.thickness}px * ${$scaleY})`};
  width: ${({ $scaleX, $width }) => `calc(${$width}px * ${$scaleX})`};
  height: ${({ shelf, $scaleY }) =>
    `calc(${shelf.type === "HANGCELL" ? shelf.getHeight() : shelf.getHeight() - shelf.thickness}px * ${$scaleY})`};
  border-left: 1px solid ${Color.alertMediumIcon};
  ${hatch}
  opacity: 0.3;
  pointer-events: none;
  z-index: 3;
`;

interface Props {
  scaleX: number;
  scaleY: number;
  shelves: readonly PlanogramShelf[];
}

export const NonMerchandisableArea = ({ scaleX, scaleY, shelves }: Props) => {
  const { width, xOffset } = getMergedNonMerchandisableArea(shelves);
  const shelf = shelves[0];

  if (!shelf || width === 0 || shelf.getHeight() === 0) {
    return null;
  }

  const isHangcell = shelf.type === "HANGCELL";
  const extensionHeight = isHangcell ? shelf.getHeight() : shelf.getHeight() - shelf.thickness;

  return (
    <>
      <ShelfPart
        $scaleX={scaleX}
        $scaleY={scaleY}
        $width={width}
        aria-hidden
        data-testid={`non-merchandisable-area-${shelf.uniqueId}`}
        data-width={width}
        data-x-offset={xOffset}
        shelf={shelf}
      />
      {extensionHeight > 0 && (
        <ExtensionPart
          $scaleX={scaleX}
          $scaleY={scaleY}
          $width={width}
          aria-hidden
          data-testid={`non-merchandisable-area-extension-${shelf.uniqueId}`}
          data-width={width}
          data-x-offset={xOffset}
          shelf={shelf}
        />
      )}
    </>
  );
};
